import { ReportStatus } from './report.model';
import { Unit, UnitWithChecklist } from './unit.model';
import { ImmutableMap, Prop, QueryParams, RequiredOnly, isNumber, isObject, isString } from './utility.model';

export const YES_NO_VALUE_EN = ['yes', 'no'] as const;
export const YES_NO_VALUE_DE = ['ja', 'nein'] as const;
export const YES_NO_VALUE_JA = ['OK', 'NG'] as const;
export const YES_NO_VALUE = [...YES_NO_VALUE_EN, ...YES_NO_VALUE_DE, ...YES_NO_VALUE_JA] as const;
export type YesNoValue = (typeof YES_NO_VALUE)[number];

export const CONSENT_STATUS = ['none', 'registered'] as const;
export type ConsentStatus = (typeof CONSENT_STATUS)[number];
export const CONSENT_UPLOAD = ['in-progress', 'decoding', 'succeed', 'failed'] as const;
export type ConsentUpload = (typeof CONSENT_UPLOAD)[number];

export const REPORT_LANGS = ['en-GB', 'en-US', 'tl', 'th', 'hi', 'id', 'zh-CN'] as const;
export type ReportLang = (typeof REPORT_LANGS)[number];

type ProjectReport = {
  status: ReportStatus;
  version: number;
};

type Consent = {
  consentId?: string;
  consentFormId?: string;
  name?: string;
  status?: ConsentStatus;
  signatory?: string;
  consentedAt?: string;
  upload?: ConsentUpload;
  decryptionKey?: string;
};

type ProjectRequiredProps = {
  organizationId: string;
  name: string;
  site: string;
  scheduledAt: number;
};

type ProjectOptionalProps = {
  remarks?: string;
};

type ProjectProvidedProps = {
  projectId: string;
  units?: Unit[];
  createdAt: number;
  author: string;
  assignedUsers: string[];
  updatedAt: number;
  consent: Consent;
  version: number;
  report: ProjectReport;
};

export type ProjectQueryParams = QueryParams & { organizationId?: string; scheduledFrom: string; scheduledTo: string };

export type ProjectCreateParams = ProjectRequiredProps & ProjectOptionalProps & { organizationId: string };

export type ProjectUpdateParams = Partial<Omit<ProjectRequiredProps, 'organizationId'>> & ProjectOptionalProps;

export type ReportCreateParams = {
  lang: ReportLang;
  departmentName?: string;
  date: string;
  units: string[];
};

export type Project = ProjectRequiredProps & ProjectOptionalProps & ProjectProvidedProps;

export type ProjectWithChecklist = Project & { units: UnitWithChecklist[] };

export function isProjectCreateParams(value: unknown): value is ProjectCreateParams {
  const sProps: Prop<RequiredOnly<ProjectCreateParams>, string>[] = ['organizationId', 'name', 'site'];
  const nProps: Prop<RequiredOnly<ProjectCreateParams>, number>[] = ['scheduledAt'];
  return isObject(value) && sProps.every(prop => isString(value[prop])) && nProps.every(prop => isNumber(value[prop]));
}

export function isProjectUpdateParams(value: unknown): value is ProjectUpdateParams {
  const sProps: Prop<ProjectUpdateParams, string>[] = ['name', 'site', 'remarks'];
  const nProps: Prop<ProjectUpdateParams, number>[] = ['scheduledAt'];
  return isObject(value) && (sProps.some(prop => isString(value[prop])) || nProps.some(prop => isNumber(value[prop])));
}

export class Projects extends ImmutableMap<string, Project> {
  constructor(projects?: Project[] | Iterable<Project>) {
    super((project: Project) => project.projectId, projects);
  }

  protected clone(projects?: Project[] | Iterable<Project>): this {
    return new Projects(projects) as this;
  }
}
